import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chatApi } from "lib/api";
import type {
  ChatInboxBucket,
  ChatSettings,
  CreateChatConversationInput,
  SendChatMessageInput,
  ShareChatEntityInput,
} from "types/chat";

export const chatQueryKeys = {
  conversations: (bucket: ChatInboxBucket | "all", search = "") =>
    ["chat", "conversations", bucket, search] as const,
  conversation: (publicId: string) => ["chat", "conversation", publicId] as const,
  messages: (publicId: string) => ["chat", "messages", publicId] as const,
  thread: (messagePublicId: string) => ["chat", "thread", messagePublicId] as const,
  requests: () => ["chat", "requests"] as const,
  unread: () => ["chat", "unread"] as const,
  settings: () => ["chat", "settings"] as const,
  recipientSearch: (query: string) => ["chat", "recipient-search", query] as const,
};

const invalidateChatLists = async (queryClient: ReturnType<typeof useQueryClient>): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] }),
    queryClient.invalidateQueries({ queryKey: chatQueryKeys.requests() }),
    queryClient.invalidateQueries({ queryKey: chatQueryKeys.unread() }),
  ]);
};

export const useChatConversationsQuery = (bucket: ChatInboxBucket | "all", search = "", enabled = true) =>
  useQuery({
    queryKey: chatQueryKeys.conversations(bucket, search),
    queryFn: () => chatApi.getConversations({ bucket, search, limit: 100 }),
    select: (response) => response.conversations,
    enabled,
  });

export const useChatConversationQuery = (publicId: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: publicId ? chatQueryKeys.conversation(publicId) : ["chat", "conversation", "empty"],
    queryFn: () => chatApi.getConversation(publicId as string),
    enabled: enabled && typeof publicId === "string" && publicId.trim().length > 0,
  });

export const useChatMessagesQuery = (publicId: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: publicId ? chatQueryKeys.messages(publicId) : ["chat", "messages", "empty"],
    queryFn: () => chatApi.getMessages(publicId as string),
    select: (response) => response.messages,
    enabled: enabled && typeof publicId === "string" && publicId.trim().length > 0,
  });

export const useChatThreadQuery = (messagePublicId: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: messagePublicId ? chatQueryKeys.thread(messagePublicId) : ["chat", "thread", "empty"],
    queryFn: () => chatApi.getThread(messagePublicId as string),
    select: (response) => response.messages,
    enabled: enabled && typeof messagePublicId === "string" && messagePublicId.trim().length > 0,
  });

export const useChatRequestsQuery = (enabled = true) =>
  useQuery({
    queryKey: chatQueryKeys.requests(),
    queryFn: () => chatApi.getRequests(),
    select: (response) => response.requests,
    enabled,
  });

export const useChatUnreadCountsQuery = (enabled = true) =>
  useQuery({
    queryKey: chatQueryKeys.unread(),
    queryFn: () => chatApi.getUnreadCount(),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
  });

export const useChatSettingsQuery = (enabled = true) =>
  useQuery({
    queryKey: chatQueryKeys.settings(),
    queryFn: () => chatApi.getSettings(),
    enabled,
  });

export const useChatRecipientSearchQuery = (query: string, enabled = true) =>
  useQuery({
    queryKey: chatQueryKeys.recipientSearch(query),
    queryFn: () => chatApi.searchRecipients(query),
    enabled: enabled && query.trim().length > 1,
    staleTime: 30_000,
  });

export const useCreateChatConversationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateChatConversationInput) => chatApi.createConversation(payload),
    onSuccess: async (conversation) => {
      await invalidateChatLists(queryClient);
      await queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversation.public_id) });
    },
  });
};

export const useUpdateChatConversationMutation = (publicId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      title?: string | null;
      hidden?: boolean;
      pinned?: boolean;
      muted_until?: string | null;
    }) => chatApi.updateConversation(publicId, payload),
    onSuccess: async (conversation) => {
      await invalidateChatLists(queryClient);
      await queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversation.public_id) });
    },
  });
};

export const useSendChatMessageMutation = (conversationPublicId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendChatMessageInput) => chatApi.sendMessage(conversationPublicId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.messages(conversationPublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversationPublicId) }),
      ]);
      await invalidateChatLists(queryClient);
    },
  });
};

export const useReplyInThreadMutation = (messagePublicId: string, conversationPublicId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendChatMessageInput) => chatApi.replyInThread(messagePublicId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.thread(messagePublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.messages(conversationPublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversationPublicId) }),
      ]);
      await invalidateChatLists(queryClient);
    },
  });
};

export const useShareChatEntityMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ShareChatEntityInput) => chatApi.shareEntity(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.unread() }),
      ]);
    },
  });
};

export const useEditChatMessageMutation = (conversationPublicId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { messagePublicId: string; body_html: string; body_rich?: Record<string, unknown> | null }) =>
      chatApi.editMessage(payload.messagePublicId, {
        body_html: payload.body_html,
        body_rich: payload.body_rich ?? null,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.messages(conversationPublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversationPublicId) }),
      ]);
      await invalidateChatLists(queryClient);
    },
  });
};

export const useDeleteChatMessageMutation = (conversationPublicId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messagePublicId: string) => chatApi.deleteMessage(messagePublicId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.messages(conversationPublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversationPublicId) }),
      ]);
      await invalidateChatLists(queryClient);
    },
  });
};

export const useChatReactionMutation = (conversationPublicId: string, mode: "add" | "remove") => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { messagePublicId: string; reactionKey: string }) =>
      mode === "add"
        ? chatApi.addReaction(payload.messagePublicId, payload.reactionKey)
        : chatApi.removeReaction(payload.messagePublicId, payload.reactionKey),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatQueryKeys.messages(conversationPublicId) });
    },
  });
};

export const useChatRequestActionMutation = (action: "accept" | "decline" | "block") => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) => {
      if (action === "accept") {
        return chatApi.acceptRequest(requestId);
      }
      if (action === "decline") {
        return chatApi.declineRequest(requestId);
      }
      return chatApi.blockRequest(requestId);
    },
    onSuccess: async () => {
      await invalidateChatLists(queryClient);
    },
  });
};

export const useMarkChatReadMutation = (conversationPublicId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReadMessagePublicId: string) => chatApi.markRead(conversationPublicId, lastReadMessagePublicId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(conversationPublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.unread() }),
        queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] }),
      ]);
    },
  });
};

export const useUpdateChatSettingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<ChatSettings> & {
      notification_type?: string;
      active?: boolean;
      channel?: string;
    }) => chatApi.updateSettings(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatQueryKeys.settings() });
    },
  });
};
